/** Mirrors of the C# audit DTO records — backend reference §8. */

export interface AuditLogListItem {
  id: number;
  /** UTC. */
  timestamp: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  ipAddress: string | null;
  httpMethod: string | null;
  path: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityDisplay: string | null;
  statusCode: number | null;
  succeeded: boolean;
  durationMs: number | null;
}

/**
 * One entry in full — every column the pipeline captured. The profile page shows all of
 * it; the detail modal shows the request-context subset the reference specifies (§13).
 */
export interface AuditLogDetail extends AuditLogListItem {
  /**
   * The record the action was scoped to — the parent of `entityId` where the two differ.
   *
   * A section of a record is a child table, so a write to one carries the CHILD's id; this is
   * what a record's own history is found by. Detail only: the list row stays lean.
   */
  parentEntityId: string | null;
  userAgent: string | null;
  errorMessage: string | null;
  correlationId: string | null;
  /** Module the action belongs to — the controller name, lowercased. */
  category: string | null;
  /** Comma-separated roles the actor held at the moment of the action. */
  roles: string | null;
  /** JWT `jti`, so one session's actions can be traced end to end. */
  tokenId: string | null;
  /** Sanitized server-side: sensitive parameter values arrive as `***`. */
  queryString: string | null;
  /** Full `X-Forwarded-For` chain as received. */
  forwardedFor: string | null;
  origin: string | null;
  referer: string | null;
  /** JSON array of EntityChange records. */
  changes: string | null;
}

export type ChangeState = 'Added' | 'Modified' | 'Deleted';

/** One entity mutated in the request. */
export interface EntityChange {
  entityType: string;
  display: string | null;
  key: string | null;
  state: ChangeState;
  /** Null on insert. */
  originalValues: Record<string, string | null> | null;
  /** Null on delete; on update, only the fields that moved. */
  newValues: Record<string, string | null> | null;
}

/**
 * Parses the `changes` payload defensively.
 *
 * Rows written before the payload took its current shape do not match, and an audit trail
 * must never be the thing that crashes a page — an unreadable payload renders as "no
 * recorded changes" rather than taking the modal down.
 */
export function parseChanges(raw: string | null): EntityChange[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (entry): entry is EntityChange =>
        typeof entry === 'object' && entry !== null && 'entityType' in entry && 'state' in entry,
    );
  } catch {
    return [];
  }
}
